import React, { useEffect, useRef } from 'react';
import { Alert, Box, Button, Checkbox, FormControl, FormControlLabel, FormHelperText, Link, Stack, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useTranslation } from '../../context/Internationalization';
import RegistrationClubFields from './RegistrationClubFields';
import RegistrationPhotoField from './RegistrationPhotoField';
import { isRecoveringRegistration } from './registrationProgress';
import AuthTextField from '../Login/AuthTextField';

export default function RegistrationForm({ values, email, errors, error, busy, progress, logoPreview, photoPreview, photoSelected,
  step, onBack, onChange, onLogo, onPhoto, onRemoveLogo, onSubmit, onSignOut }) {
  const { t } = useTranslation();
  const heading = useRef(null);
  const previousStep = useRef(step);
  const recovering = isRecoveringRegistration(progress);
  const uncertain = ['creating', 'uncertain'].includes(progress?.phase);
  const clubStep = recovering || step === 'club';
  useEffect(() => {
    if (previousStep.current !== step) {
      heading.current?.focus({ preventScroll: true });
      heading.current?.scrollIntoView?.({ block: 'nearest' });
      previousStep.current = step;
    }
  }, [step]);

  return <Stack component="form" noValidate onSubmit={onSubmit} spacing={3} aria-busy={busy} aria-labelledby="registration-heading">
    <Box>
      <Typography id="registration-heading" ref={heading} tabIndex={-1} component="h1" variant="h4"
        sx={{ fontWeight: 700, fontSize: 30, lineHeight: 1.3, mb: 1.5 }}>
        {t(recovering ? 'registration_finish_setup' : clubStep ? 'registration_create_club' : 'registration_create_profile')}
      </Typography>
      <Typography color="text.secondary" variant="body2">
        {t(clubStep ? 'registration_club_step_description' : 'registration_admin_step_description')}
      </Typography>
    </Box>
    {error && <Alert severity="error" role="alert">{t(error)}</Alert>}
    {progress?.clubId && <Alert severity="info">{t('registration_club_created')}</Alert>}
    {clubStep ? <RegistrationClubFields values={values} errors={errors} busy={busy} progress={progress}
      logoPreview={logoPreview} onChange={onChange} onLogo={onLogo} onRemoveLogo={onRemoveLogo} /> :
      <Stack spacing={2}>
        <RegistrationPhotoField preview={photoPreview} selected={photoSelected} error={errors.photo} busy={busy} onChange={onPhoto} />
        {['firstName', 'lastName'].map((name) => <AuthTextField key={name} required
          label={t(name === 'firstName' ? 'registration_first_name' : 'registration_last_name')} name={name}
          autoComplete={name === 'firstName' ? 'given-name' : 'family-name'} value={values[name]} disabled={busy}
          onChange={(event) => onChange(name, event.target.value)} error={Boolean(errors[name])}
          helperText={errors[name] ? t(errors[name]) : ''} inputProps={{ maxLength: 100 }} />)}
        <FormControl error={Boolean(errors.termsAccepted)}>
          <FormControlLabel sx={{ alignItems: 'flex-start', mx: 0 }} control={<Checkbox checked={values.termsAccepted}
            sx={{ pl: 0 }} disabled={busy} name="termsAccepted" inputProps={{ 'aria-invalid': Boolean(errors.termsAccepted),
              'aria-describedby': errors.termsAccepted ? 'registration-terms-error' : undefined }}
            onChange={(event) => onChange('termsAccepted', event.target.checked)} />}
            label={<Typography component="span" variant="body2" sx={{ display: 'block', pt: 1 }}>{t('registration_agree')}{' '}
              <Link href="https://www.playerdex.app/terms" target="_blank" rel="noopener noreferrer">{t('registration_terms')}</Link>{' '}
              {t('registration_and')}{' '}<Link href="https://www.playerdex.app/privacy" target="_blank" rel="noopener noreferrer">{t('registration_privacy')}</Link>.
            </Typography>} />
          {errors.termsAccepted && <FormHelperText id="registration-terms-error" role="alert" sx={{ mx: 0 }}>{t(errors.termsAccepted)}</FormHelperText>}
        </FormControl>
      </Stack>}
    <Stack spacing={2}>
      <LoadingButton variant="contained" type="submit" loading={busy} fullWidth>
        {t(uncertain ? 'registration_check_again' : recovering ? 'registration_finish_setup' : clubStep ? 'registration_continue' : 'registration_complete_setup')}
      </LoadingButton>
      {!clubStep && <Button variant="outlined" type="button" disabled={busy} startIcon={<ArrowBackIcon />} onClick={onBack}>
        {t('registration_back')}
      </Button>}
      <Stack direction="row" alignItems="baseline" justifyContent="center" spacing={.5} flexWrap="wrap" useFlexGap>
        <Typography component="span" variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', minWidth: 0 }}
          aria-label={`${t('registration_verified_email')}: ${email}`}>{email}</Typography>
        <Button variant="text" size="small" onClick={onSignOut} disabled={busy}
          sx={{ minWidth: 0, p: .5, textDecoration: 'underline' }}>{t('registration_sign_out')}</Button>
      </Stack>
    </Stack>
  </Stack>;
}
